import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { useSelector } from "react-redux"
import { RootState } from "../store"
import { RosterPlayer, SpecRole } from "web/data/models"
import { getCurrentBuild } from 'web/data/core-data'

export type RosterState = {
    players: RosterPlayer[]
}

const initialState: RosterState = {
    players: []
}

export const slice = createSlice({
    name: "roster",
    initialState,
    reducers: {

        addPlayer: (state, action: PayloadAction<{ player: RosterPlayer }>) => {
            state.players.push(action.payload.player)
        },

        removePlayer: (state, action: PayloadAction<{ playerId: string }>) => {
            state.players = state.players.filter(player => player.id !== action.payload.playerId)
        },

        updatePlayer: (state, action: PayloadAction<{ player: RosterPlayer }>) => {
            state.players = [
                ...state.players.filter(p => p.id !== action.payload.player.id),
                action.payload.player
            ]
        },

        prunePlayers: (state) => {

            // This always runs against current
            const buildData = getCurrentBuild()

            // Any specs not present in the build-data are deemed
            // useless and can be safely removed. This is most likely
            // to occur if a spec lost all its utility.
            state.players = state.players.filter(player => buildData.specsById[player.specId] != null)

            // Process each remaining player and prune further
            state.players.map(player => {

                // Prune selected talents, simple id check, not very reliable,
                // talent could still be intact but have a new ID, won't break
                // anything though, is just inconvenient for the user
                player.talentIds = player.talentIds
                    .filter(talentId => buildData.Talent(talentId) != null)

                // Prune selected modifiers
                player.modifierIds = player.modifierIds
                    .filter(modifierId => {
                        const mod = buildData.Modifier(modifierId)

                        // Modifier is gone, remove it from the player
                        if (mod == null) {
                            return false
                        }

                        // Modifier is no longer applicable to this class,
                        // remove it
                        if (mod.eligibleSpecIds.includes(player.specId) === false) {
                            return false
                        }

                        return true

                    })

                // Prune overrides, simple spell-id presence check, not very
                // reliable, spell could still be intact but have a new ID,
                // won't break anything though, is just inconvenient for the
                // user
                player.overrides = player.overrides.filter(override => {
                    return buildData.Spell(override.spellId) != null
                })

                player.patchInfo = buildData.patchInfo
                return player

            })

        },

    },
    extraReducers: {
        // Empty
    }
})

function selectors(state: RosterState) {
    return {
        // TODO: Util func, not selector, move me plz
        getPlayersByRole: (players: RosterPlayer[]) => {
            return players.reduce<{
                tanks: RosterPlayer[]
                healers: RosterPlayer[]
                melee: RosterPlayer[]
                ranged: RosterPlayer[]
            }>((acc, curr) => {

                const specRole = getCurrentBuild().Spec(curr.specId).role

                if (specRole === SpecRole.Tank) {
                    acc.tanks.push(curr)
                }
                if (specRole === SpecRole.Healer) {
                    acc.healers.push(curr)
                }
                if (specRole === SpecRole.Melee) {
                    acc.melee.push(curr)
                }
                if (specRole === SpecRole.Ranged) {
                    acc.ranged.push(curr)
                }

                return acc

            }, {
                tanks: [],
                healers: [],
                melee: [],
                ranged: []
            })
        },
    }
}

export const RosterActions = slice.actions
export const RosterReducer = slice.reducer

export const useRosterState = () => useSelector<RootState, RosterState>(state => state.roster)

export const useRosterSelectors = () => {
    const state = useSelector<RootState, RosterState>(state => state.roster)
    return selectors(state)
}
